import { CapacitorWifiConnect, ConnectState } from '@falconeta/capacitor-wifi-connect';

import { IS_IOS } from '../../../../../common/constants';
import { timeout } from '../../../../../utils/common/common-utils';
import { WAIT_FOR_NATIVE_WIFI_MS } from './constants';

type WifiDeviceConnectOptions = {
  ssid: string;
  password: string;
};

export async function connectToSelectedNetwork(currentSsid: string, homeSsid: string, password?: string) {
  const isOnHomeSsid = currentSsid === homeSsid;

  // Force connect only if not already on the desired home network.
  if (!isOnHomeSsid) {
    const attemptConnect = async () => {
      if (password) {
        await CapacitorWifiConnect.secureConnect({
          ssid: homeSsid,
          password,
          saveNetwork: false,
        });
      } else {
        await CapacitorWifiConnect.connect({ ssid: homeSsid });
      }
    };

    try {
      await attemptConnect();
    } catch (e) {
      // Verify current SSID.
      const current = await CapacitorWifiConnect.getDeviceSSID().catch(() => undefined);
      const currentSsid = current?.value;
      if (currentSsid !== homeSsid) {
        await attemptConnect();
      }
    }

    await timeout(3000);
  }
}

export async function reconnectToHomeWifi(homeSsid: string, password: string) {
  if (!IS_IOS) {
    await CapacitorWifiConnect.disconnect();
    return;
  }

  // iOS disconnect() can't resolve the device WAP SSID, so it leaves the phone on the WAP.
  try {
    const current = await CapacitorWifiConnect.getDeviceSSID();
    await connectToSelectedNetwork(current?.value ?? '', homeSsid, password);
  } catch (e) {
    // Registration supports manual reconnection without repeating device-local setup.
    console.error(e);
  }
}

export async function connectToWifiDevice({ ssid, password }: WifiDeviceConnectOptions): Promise<ConnectState> {
  if (!IS_IOS) {
    const { value: connectState } = await CapacitorWifiConnect.secureConnect({ ssid, password });
    if (connectState === ConnectState.Ok) {
      await timeout(WAIT_FOR_NATIVE_WIFI_MS);
    }
    return connectState;
  }

  const current = await CapacitorWifiConnect.getDeviceSSID();
  await connectToSelectedNetwork(current?.value ?? '', ssid, password);

  try {
    return (await CapacitorWifiConnect.getDeviceSSID()).value === ssid ? ConnectState.Ok : ConnectState.Ko;
  } catch {
    return ConnectState.Ko;
  }
}
